import React from "react";

import { Box, Field, Input, Textarea } from "@metrea-ui/v2";
import { mColor } from "src/utils/themeUtils";

// Shared plain text/textarea helper for the stable direct-v2 field pattern that
// now repeats across multiple edit screens. Numeric/select variants stay local.
const FormTextField = ({
	id,
	name,
	label,
	value,
	onChange,
	disabled = false,
	required = false,
	errorText = "",
	width = "100%",
	maxLength,
	placeholder,
	multiline = false,
	rows = 3,
	size = "sm",
	dataTestId,
	minControlHeight,
	endAdornment,
	reserveHelperSpace = true,
	helperColor = mColor.Danger,
	marginTop = "1rem",
	marginBottom = "0.5rem",
}) => {
	const invalid = Boolean(errorText);
	const helpId = `${id}-helptext`;
	const helperVisible = Boolean(errorText);
	const showHelperRow = reserveHelperSpace || helperVisible;
	const describedBy = showHelperRow ? helpId : undefined;
	const fontSize = size === "sm" ? "0.8rem" : "0.875rem";
	const controlStyle = {
		width: "100%",
		fontSize,
		minHeight: minControlHeight,
	};

	return (
		<Box width={width} mt={marginTop} mb={marginBottom}>
			<Field.Root required={required} invalid={invalid} style={{ width: "100%" }}>
				<Field.Label htmlFor={id}>
					{label}
					{required ? <Field.RequiredIndicator /> : null}
				</Field.Label>
				<Box position="relative" width="100%">
					{multiline ? (
						<Textarea
							id={id}
							data-testid={dataTestId}
							name={name}
							value={value ?? ""}
							rows={rows}
							disabled={disabled}
							placeholder={placeholder}
							aria-invalid={invalid ? "true" : undefined}
							aria-describedby={describedBy}
							onChange={onChange}
							size={size}
							variant="subtle"
							maxLength={maxLength}
							style={{
								...controlStyle,
								lineHeight: 1.5,
								resize: "vertical",
							}}
						/>
					) : (
						<Input
							id={id}
							data-testid={dataTestId}
							name={name}
							value={value ?? ""}
							onChange={onChange}
							disabled={disabled}
							placeholder={placeholder}
							size={size}
							variant="subtle"
							aria-invalid={invalid ? "true" : undefined}
							aria-describedby={describedBy}
							maxLength={maxLength}
							style={{
								...controlStyle,
								paddingRight: endAdornment ? 40 : undefined,
							}}
						/>
					)}
					{!multiline && endAdornment ? (
						<Box
							position="absolute"
							top="50%"
							right="8px"
							transform="translateY(-50%)"
							display="flex"
							alignItems="center"
							pointerEvents="auto"
						>
							{endAdornment}
						</Box>
					) : null}
				</Box>
				{showHelperRow ? (
					<Field.HelperText id={helpId}>
						<span
							style={{
								display: "block",
								minHeight: reserveHelperSpace ? "1.5em" : undefined,
								fontSize: "0.75rem",
								color: invalid ? helperColor : "inherit",
								visibility: helperVisible ? "visible" : "hidden",
							}}
						>
							{helperVisible ? errorText : reserveHelperSpace ? "\u00A0" : ""}
						</span>
					</Field.HelperText>
				) : null}
			</Field.Root>
		</Box>
	);
};

export default FormTextField;
